/**
 * VIVE Reviews Showcase
 * Arrow + autoplay control for the horizontally scrollable review card row.
 * Namespace: scoped entirely to .vive-showcase markup.
 */

(function () {
  'use strict';

  const INITIALISED = 'viveShowcaseReady';

  function init(root) {
    const scope = root || document;
    const sections = scope.querySelectorAll('.vive-showcase');

    sections.forEach((section) => {
      if (section.dataset[INITIALISED] === 'true') return;
      section.dataset[INITIALISED] = 'true';
      setupShowcase(section);
    });
  }

  function setupShowcase(section) {
    const track = section.querySelector('[data-vive-showcase-track]');
    if (!track) return;

    const prev = section.querySelector('[data-vive-showcase-prev]');
    const next = section.querySelector('[data-vive-showcase-next]');
    const cards = track.querySelectorAll('[data-vive-showcase-card]');
    if (cards.length === 0) return;

    /**
     * Distance for one arrow click: the width of a single card plus the flex gap,
     * measured live so it stays correct across breakpoints and editor changes.
     */
    const stepDistance = () => {
      const first = track.querySelector('[data-vive-showcase-card]');
      if (!first) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const scrollByStep = (direction) => {
      track.scrollBy({ left: stepDistance() * direction, behavior: 'smooth' });
    };

    const syncArrows = () => {
      if (!prev && !next) return;

      // 1px tolerance absorbs sub-pixel scroll positions in the browser.
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= maxScroll() - 1;
      const noOverflow = maxScroll() <= 1;

      if (prev) prev.disabled = noOverflow || atStart;
      if (next) next.disabled = noOverflow || atEnd;
    };

    if (prev) prev.addEventListener('click', () => scrollByStep(-1));
    if (next) next.addEventListener('click', () => scrollByStep(1));

    track.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();

    setupAutoplay(section, track, maxScroll, scrollByStep);
  }

  function setupAutoplay(section, track, maxScroll, scrollByStep) {
    if (section.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const speed = parseInt(section.dataset.autoplaySpeed, 10) || 4000;
    let timer = null;

    const advance = () => {
      // Loop back to the first card once the row has been scrolled to its end.
      if (track.scrollLeft >= maxScroll() - 1) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollByStep(1);
      }
    };

    const start = () => {
      if (timer) return;
      timer = window.setInterval(advance, speed);
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', start);
    section.addEventListener('focusin', stop);
    section.addEventListener('focusout', start);
    track.addEventListener('touchstart', stop, { passive: true });

    // Only run while the section is actually on screen.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
        },
        { threshold: 0.2 }
      );
      observer.observe(section);
    } else {
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-initialise when the section is added or reloaded in the theme editor.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();